import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, model, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, firstValueFrom, Subject, switchMap, tap, timer } from 'rxjs';
import { catchAppError, createSearchPattern, createTokenizedSearchPattern } from 'src/app/shared/utils';
import { ContentSelectorDialog } from './dialog';
import { AppInjector } from '../../../app-injector.module';
import { VisualService } from '../../../services/api.services';

@Component({
  selector: 'content-selector-dialog',
  templateUrl: './ContentSelectorDialog.component.html',
  styleUrl: './ContentSelectorDialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContentSelectorDialogComponent<T extends ContentSelectorDialog.Data<T>> {
  readonly destroyRef = inject(DestroyRef);

  readonly dialogRef = inject<MatDialogRef<
    ContentSelectorDialogComponent<T>,
    ContentSelectorDialog.Result<T>>>(MatDialogRef);

  readonly dialogData = inject<any>(MAT_DIALOG_DATA);
  readonly config = <ContentSelectorDialog.Config<T>>this.dialogData.config;
  readonly data = <ContentSelectorDialog.Data<T>>this.dialogData.data;

  readonly icon = this.data.icon;
  readonly title = this.data.title;

  readonly cancelButton = {
    ...{ text: 'Close', color: 'outline' },
    ...this.data.cancelButton,
  };

  readonly acceptButton = {
    ...{ text: 'Select', color: 'primary' },
    ...this.data.acceptButton
  };

  readonly categories = signal<ReadonlyArray<ContentSelectorDialog.ItemCategory<T>>>([]);
  readonly itemGroups = computed(() => this.categories().flatMap(x => x.groups()));
  readonly items = signal<ReadonlyArray<ContentSelectorDialog.Item<T>>>([]);

  readonly error = signal(false);

  readonly itemPreview = this.config.options?.itemPreview;
  readonly suggestOption = this.data?.suggestOption;

  readonly favouriteOnly = signal(false);

  readonly filter = model('');
  readonly filterPatterns = computed<RegExp[]>(() => createTokenizedSearchPattern(this.filter()));
  readonly filtering = computed(() => !!this.filterPatterns()?.length);

  readonly selectedItemDiv = viewChild('selectedItemDiv', { read: ElementRef<HTMLDivElement> });
  
  // Track whether we should scroll to the selected item.
  // This is set on load, to show the pre-selected item (if any)
  // This is unset on click and once consumed
  readonly scrollToSelectedItem = signal(false);

  readonly originalSelectedItemId = signal(null);

  readonly disabledItemIds = signal<ReadonlySet<any>>(
    this.data.disabledItemIds.reduce((s, x) => s.add(x), new Set<any>()));

  readonly selectedItemDisabled = computed(() =>
    this.disabledItemIds().has(this.selectedItem()?.id));

  // Can accept only if 
  readonly canAccept = computed(() => {
    let [selectedItem, originalSelectedItemId] =
      [this.selectedItem(), this.originalSelectedItemId()];

    if (selectedItem)
      return !originalSelectedItemId || selectedItem.id != originalSelectedItemId;

    return false;
  });

  constructor() {
    effect(() => {
      let [selectedItemDiv, scrollToSelectedItem] = [
        <HTMLDivElement>this.selectedItemDiv()?.nativeElement,
        this.scrollToSelectedItem()
      ];

      if (selectedItemDiv && scrollToSelectedItem) {
        untracked(() => this.scrollToSelectedItem.set(false));

        let scrollParent = selectedItemDiv.parentElement;
        scrollParent.scrollIntoView();
      }
    });

    effect(() => {
      let favouriteOnly = this.favouriteOnly();
      untracked(() => {
        if (favouriteOnly)
          this.items().forEach(i => i.hideByFavouritesOnly.set(!i.favourite()));
        else
          this.items().forEach(i => i.hideByFavouritesOnly.set(false));
      });
    });

    effect(() => {
      let filtering = this.filtering();
      untracked(() => {
        if (!filtering)
          this.items().forEach(i => i.hideByFiltering.set(false));
      });
    });
  }

  ngOnInit() {
    this.load();
  }


  readonly loading = signal(false);

  private load() {
    this.loading.set(true);
    this.error.set(false);

    this.config.source(this.data.context).pipe(
      takeUntilDestroyed(this.destroyRef),

      switchMap((items) => {
        let categories = this.config.categories()
          .filter(c => !this.data.disabledCategories || this.data.disabledCategories?.includes(c.id));

        this.categories.set(categories);

        return timer(0).pipe(
          tap(() => {
            let allItems: ContentSelectorDialog.Item<T>[] = [];

            for (let item of items) {
              let category = categories.find(x => x.id == item.categoryId);
              if (!category)
                continue;

              item.category.set(category);

              category._items.push(item);
              allItems.push(item);
            }

            categories.forEach(c => c.init());
            allItems.forEach(i => i.init());
            allItems.sort((a, b) => a.title.localeCompare(b.title));

            this.items.set(allItems);

            let itemToSelect =
             (this.data.selectedItemKey ? allItems.find(x => x.key == this.data.selectedItemKey) : null) ?? 
             (this.data.selectedItemId  ? allItems.find(x => x.id  == this.data.selectedItemId ) : null) ?? categories[0]._items[0];

            if (itemToSelect) {
              this.selectItem(itemToSelect);
            }
            else if (this.data.selectedCategory) {
              this.selectCategory(categories.find(x => x.id == this.data.selectedCategory) ?? categories[0]);
            }
            else {
              this.selectCategory(categories[0]);
            }

            let selectedItem = this.selectedItem();
            if (selectedItem && (selectedItem.id  == this.data.selectedItemId)) {
              this.originalSelectedItemId.set(selectedItem.id);
              this.scrollToSelectedItem.set(true);
            }
          }));
      }),

      catchAppError(() => {
        this.error.set(true);
      }, { showErrorSnack: false }),

      finalize(() => this.loading.set(false))
    ).subscribe();
  }


  cancelClick() {
    this.dialogRef.close(null);
  }

  acceptClick() {
    this.dialogRef.close({
      filter: this.filter(),
      selectedCategory: this.selectedCategory()?.id,
      selectedItem: this.selectedItem()?.data,
      selectedItemKey: this.selectedItem()?.key
    });
  }
  
  selectedCategory = signal<ContentSelectorDialog.ItemCategory<T>>(null);
  selectedItemGroup = signal<ContentSelectorDialog.ItemGroup<T>>(null);
  selectedItem = signal<ContentSelectorDialog.Item<T>>(null);

  private _setSelectedCategory(c: ContentSelectorDialog.ItemCategory<T>) {
    this.selectedCategory()?.selected.set(false);
    c?.selected.set(true);
    this.selectedCategory.set(c);
  }

  private _setSelectedItemGroup(g: ContentSelectorDialog.ItemGroup<T>) {
    this.selectedItemGroup()?.selected.set(false);
    g?.selected.set(true);
    this.selectedItemGroup.set(g);
  }

  private _setSelectedItem(i: ContentSelectorDialog.Item<T>) {
    this.selectedItem()?.selected.set(false);
    i?.selected.set(true);
    this.selectedItem.set(i);

    if (i && !i.viewed()) {
      i.viewed.set(true);
    }
  }

  selectCategory(c: ContentSelectorDialog.ItemCategory<T>) {
    this._setSelectedCategory(c);
    let g = c?.visibleItemGroups()[0];
    this.selectItemGroup(g);
  }

  selectItemGroup(g: ContentSelectorDialog.ItemGroup<T>) {
    this._setSelectedItemGroup(g);
    let i = g?.visibleItems()[0];
    this._setSelectedItem(i);
  }

  selectItem(i: ContentSelectorDialog.Item<T>) {
    this.scrollToSelectedItem.set(false);

    if (this.selectedCategory() != i?.category()) {
      this._setSelectedCategory(i?.category());
    }

    if (!i?.groups()?.includes(this.selectedItemGroup())) {
      this._setSelectedItemGroup(i?.groups()[0]);
    }

    this._setSelectedItem(i);
  }
}
